'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { useSession } from 'next-auth/react'
import type { PlayerTrack } from './player-context'

const KEY_FAV = 'blc.favorites.v1'
const KEY_RECENT = 'blc.recents.v1'
const KEY_PLAYLISTS = 'blc.playlists.v1'
const KEY_MERGED = 'blc.favorites.merged.v1'
const MAX_RECENTS = 50

export type Playlist = {
  id: string
  title: string
  createdAt: number
  tracks: PlayerTrack[]
}

function read<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch { return fallback }
}

function write<T>(key: string, value: T) {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(key, JSON.stringify(value)) } catch {}
  // notifie autres composants (cross-tab + same-tab)
  window.dispatchEvent(new CustomEvent('blc-library', { detail: { key } }))
}

function useStored<T>(key: string, fallback: T): [T, (v: T | ((p: T) => T)) => void] {
  const [v, setV] = useState<T>(fallback)
  useEffect(() => { setV(read<T>(key, fallback)) }, [key])
  useEffect(() => {
    const onChange = (e: Event) => {
      const custom = e as CustomEvent<{ key: string }>
      if ((e as StorageEvent).key === key || custom.detail?.key === key) setV(read<T>(key, fallback))
    }
    window.addEventListener('storage', onChange)
    window.addEventListener('blc-library', onChange)
    return () => {
      window.removeEventListener('storage', onChange)
      window.removeEventListener('blc-library', onChange)
    }
  }, [key])
  const update = useCallback((next: T | ((p: T) => T)) => {
    setV(prev => {
      const value = typeof next === 'function' ? (next as (p: T) => T)(prev) : next
      write(key, value)
      return value
    })
  }, [key])
  return [v, update]
}

/**
 * Hook favoris : si l'utilisateur est connecté → utilise l'API DB côté serveur.
 * Sinon → fallback localStorage. Au premier login, on migre automatiquement
 * les favoris locaux vers la DB (one-shot).
 */
export function useFavorites() {
  const { status, data: session } = useSession()
  const userId = (session?.user as any)?.id as string | undefined
  const [localFavs, setLocalFavs] = useStored<PlayerTrack[]>(KEY_FAV, [])
  const [serverFavs, setServerFavs] = useState<PlayerTrack[]>([])
  const [serverLoaded, setServerLoaded] = useState(false)
  const mergedFor = useRef<string | null>(null)

  const isLogged = status === 'authenticated' && !!userId

  // Récupère les favoris du serveur quand connecté
  const refresh = useCallback(async () => {
    if (!isLogged) return
    try {
      const r = await fetch('/api/favorites', { cache: 'no-store' })
      if (r.ok) {
        const j = await r.json()
        setServerFavs(j.items || [])
        setServerLoaded(true)
      }
    } catch {/* */}
  }, [isLogged])

  useEffect(() => { refresh() }, [refresh])

  // Migration one-shot : à la première connexion, on push les favoris locaux en DB
  useEffect(() => {
    if (!isLogged || !userId) return
    const key = `${KEY_MERGED}:${userId}`
    if (typeof window === 'undefined') return
    if (mergedFor.current === userId) return
    if (localStorage.getItem(key)) { mergedFor.current = userId; return }
    if (localFavs.length === 0) {
      localStorage.setItem(key, '1'); mergedFor.current = userId; return
    }
    fetch('/api/favorites/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: localFavs }),
    }).then(r => {
      if (r.ok) {
        localStorage.setItem(key, '1')
        mergedFor.current = userId
        refresh()
      }
    }).catch(() => {})
  }, [isLogged, userId, localFavs, refresh])

  const favs = isLogged ? serverFavs : localFavs

  const has = useCallback((videoId: string) => favs.some(t => t.videoId === videoId), [favs])

  const toggle = useCallback(async (t: PlayerTrack) => {
    if (isLogged) {
      const exists = serverFavs.some(x => x.videoId === t.videoId)
      // optimistic update
      setServerFavs(prev => exists
        ? prev.filter(x => x.videoId !== t.videoId)
        : [t, ...prev])
      if (exists) {
        await fetch(`/api/favorites?videoId=${encodeURIComponent(t.videoId)}`, { method: 'DELETE' })
      } else {
        await fetch('/api/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(t),
        })
      }
    } else {
      setLocalFavs(prev => prev.some(x => x.videoId === t.videoId)
        ? prev.filter(x => x.videoId !== t.videoId)
        : [t, ...prev])
    }
  }, [isLogged, serverFavs, setLocalFavs])

  const remove = useCallback(async (videoId: string) => {
    if (isLogged) {
      setServerFavs(prev => prev.filter(t => t.videoId !== videoId))
      await fetch(`/api/favorites?videoId=${encodeURIComponent(videoId)}`, { method: 'DELETE' })
    } else {
      setLocalFavs(prev => prev.filter(t => t.videoId !== videoId))
    }
  }, [isLogged, setLocalFavs])

  return { favs, has, toggle, remove, isLogged }
}

/**
 * Historique : serveur si connecté (synchro multi-appareils), sinon localStorage.
 * Migration one-shot du local → DB à la 1ʳᵉ connexion.
 */
export function useRecents() {
  const { status, data: session } = useSession()
  const userId = (session?.user as any)?.id as string | undefined
  const isLogged = status === 'authenticated' && !!userId
  const [localRecents, setLocalRecents] = useStored<PlayerTrack[]>(KEY_RECENT, [])
  const [serverRecents, setServerRecents] = useState<PlayerTrack[]>([])
  const mergedFor = useRef<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isLogged) return
    try {
      const r = await fetch('/api/recents', { cache: 'no-store' })
      if (r.ok) { const j = await r.json(); setServerRecents(j.items || []) }
    } catch {}
  }, [isLogged])
  useEffect(() => { refresh() }, [refresh])

  useEffect(() => {
    if (!isLogged || !userId || typeof window === 'undefined') return
    const key = `blc.recents.merged.v1:${userId}`
    if (mergedFor.current === userId) return
    if (localStorage.getItem(key)) { mergedFor.current = userId; return }
    if (localRecents.length === 0) { localStorage.setItem(key, '1'); mergedFor.current = userId; return }
    fetch('/api/recents/merge', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: localRecents }),
    }).then(r => { if (r.ok) { localStorage.setItem(key, '1'); mergedFor.current = userId; refresh() } }).catch(() => {})
  }, [isLogged, userId, localRecents, refresh])

  const recents = isLogged ? serverRecents : localRecents

  const push = useCallback((t: PlayerTrack) => {
    if (isLogged) {
      setServerRecents(prev => [t, ...prev.filter(x => x.videoId !== t.videoId)].slice(0, MAX_RECENTS))
      fetch('/api/recents', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(t) }).catch(() => {})
    } else {
      setLocalRecents(prev => [t, ...prev.filter(x => x.videoId !== t.videoId)].slice(0, MAX_RECENTS))
    }
  }, [isLogged, setLocalRecents])

  const clear = useCallback(() => {
    if (isLogged) { setServerRecents([]); fetch('/api/recents', { method: 'DELETE' }).catch(() => {}) }
    else setLocalRecents([])
  }, [isLogged, setLocalRecents])

  return { recents, push, clear }
}

/**
 * Playlists : serveur si connecté (synchro multi-appareils), sinon localStorage.
 * Migration one-shot du local → DB à la 1ʳᵉ connexion.
 */
export function usePlaylists() {
  const { status, data: session } = useSession()
  const userId = (session?.user as any)?.id as string | undefined
  const isLogged = status === 'authenticated' && !!userId
  const [localPlaylists, setLocalPlaylists] = useStored<Playlist[]>(KEY_PLAYLISTS, [])
  const [serverPlaylists, setServerPlaylists] = useState<Playlist[]>([])
  const mergedFor = useRef<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isLogged) return
    try {
      const r = await fetch('/api/playlists', { cache: 'no-store' })
      if (r.ok) { const j = await r.json(); setServerPlaylists(j.items || []) }
    } catch {}
  }, [isLogged])
  useEffect(() => { refresh() }, [refresh])

  useEffect(() => {
    if (!isLogged || !userId || typeof window === 'undefined') return
    const key = `blc.playlists.merged.v1:${userId}`
    if (mergedFor.current === userId) return
    if (localStorage.getItem(key)) { mergedFor.current = userId; return }
    if (localPlaylists.length === 0) { localStorage.setItem(key, '1'); mergedFor.current = userId; return }
    fetch('/api/playlists/merge', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playlists: localPlaylists }),
    }).then(r => { if (r.ok) { localStorage.setItem(key, '1'); mergedFor.current = userId; refresh() } }).catch(() => {})
  }, [isLogged, userId, localPlaylists, refresh])

  const playlists = isLogged ? serverPlaylists : localPlaylists

  const create = useCallback((title: string) => {
    const id = `pl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const p: Playlist = { id, title: title.trim() || 'Sans titre', createdAt: Date.now(), tracks: [] }
    if (isLogged) {
      setServerPlaylists(prev => [p, ...prev])
      fetch('/api/playlists', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, title: p.title }) }).catch(() => {})
    } else {
      setLocalPlaylists(prev => [p, ...prev])
    }
    return id
  }, [isLogged, setLocalPlaylists])

  const remove = useCallback((id: string) => {
    if (isLogged) {
      setServerPlaylists(prev => prev.filter(p => p.id !== id))
      fetch(`/api/playlists?id=${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => {})
    } else setLocalPlaylists(prev => prev.filter(p => p.id !== id))
  }, [isLogged, setLocalPlaylists])

  const rename = useCallback((id: string, title: string) => {
    if (isLogged) {
      setServerPlaylists(prev => prev.map(p => p.id === id ? { ...p, title } : p))
      fetch('/api/playlists', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, title }) }).catch(() => {})
    } else setLocalPlaylists(prev => prev.map(p => p.id === id ? { ...p, title } : p))
  }, [isLogged, setLocalPlaylists])

  const addTrack = useCallback((id: string, track: PlayerTrack) => {
    const merge = (prev: Playlist[]) => prev.map(p => {
      if (p.id !== id) return p
      if (p.tracks.some(t => t.videoId === track.videoId)) return p
      return { ...p, tracks: [...p.tracks, track] }
    })
    if (isLogged) {
      setServerPlaylists(merge)
      fetch('/api/playlists/tracks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ playlistId: id, track }) }).catch(() => {})
    } else setLocalPlaylists(merge)
  }, [isLogged, setLocalPlaylists])

  const removeTrack = useCallback((id: string, videoId: string) => {
    const drop = (prev: Playlist[]) => prev.map(p => p.id === id ? { ...p, tracks: p.tracks.filter(t => t.videoId !== videoId) } : p)
    if (isLogged) {
      setServerPlaylists(drop)
      fetch(`/api/playlists/tracks?playlistId=${encodeURIComponent(id)}&videoId=${encodeURIComponent(videoId)}`, { method: 'DELETE' }).catch(() => {})
    } else setLocalPlaylists(drop)
  }, [isLogged, setLocalPlaylists])

  // Réordonne un morceau DANS une playlist : déplace l'élément `from` pour
  // l'insérer AVANT `to` (0..length). Persiste les nouvelles positions côté
  // serveur (compte connecté) — le GET /api/playlists ordonne par position.
  const reorderTrack = useCallback((id: string, from: number, to: number) => {
    const pl = playlists.find(p => p.id === id)
    if (!pl) return
    if (from < 0 || from >= pl.tracks.length || to < 0 || to > pl.tracks.length) return
    const insertAt = from < to ? to - 1 : to
    if (insertAt === from) return
    const arr = [...pl.tracks]
    const [moved] = arr.splice(from, 1)
    arr.splice(insertAt, 0, moved)
    const apply = (prev: Playlist[]) => prev.map(p => p.id === id ? { ...p, tracks: arr } : p)
    if (isLogged) {
      setServerPlaylists(apply)
      fetch('/api/playlists/tracks', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playlistId: id, order: arr.map(t => t.videoId) }),
      }).catch(() => {})
    } else setLocalPlaylists(apply)
  }, [playlists, isLogged, setLocalPlaylists])

  return { playlists, create, remove, rename, addTrack, removeTrack, reorderTrack }
}
